import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { aboutPageData } from "@/mocks/homeData";
import { useEntranceAnimation } from "@/hooks/useEntranceAnimation";
import { AboutPageJsonLd, AboutBreadcrumbJsonLd } from "@/components/feature/JsonLd";

function AboutHero() {
  const { hero } = aboutPageData;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      className="relative overflow-hidden flex items-center justify-center"
      style={{ minHeight: "100svh" }}
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://readdy.ai/api/search-image?query=Elegant%20abstract%20architectural%20space%20for%20a%20cultural%20technology%20publishing%20company%2C%20soaring%20paper%20scrolls%20dissolving%20into%20fine%20dust%20particles%20suspended%20in%20warm%20golden%20light%20beams%2C%20intersecting%20horizontal%20and%20vertical%20golden%20threads%20creating%20a%20delicate%20lattice%20structure%2C%20soft%20cream%20and%20warm%20sand%20palette%20with%20subtle%20terracotta%20and%20aged%20brass%20accents%2C%20vast%20luminous%20emptiness%20with%20floating%20translucent%20manuscript%20fragments%2C%20editorial%20illustration%20style%20with%20refined%20minimal%20composition%2C%20no%20photographic%20realism%2C%20generous%20negative%20space%20for%20text%20overlay%2C%20serene%20scholarly%20atmosphere%20blending%20tradition%20and%20modern%20innovation&width=1600&height=900&seq=qh-about-brand-02&orientation=landscape&nocache=true')",
        }}
      />
      <div className="absolute inset-0 bg-white/20" />
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/50 via-black/15 to-transparent" />
      <div className="relative z-10 text-center px-4 w-full">
        <img
          src="https://storage.readdy-site.link/project_files/6517ebfd-0186-45e8-a53b-6a7dda87aef3/6d23f5d8-f2c4-4070-a39b-51782f265262_compressed_logo-02.webp"
          alt="千華駐科技"
          className={`h-10 md:h-12 w-auto mx-auto mb-5 object-contain transition-all duration-[600ms] ease-out ${
            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "0ms" }}
        />
        <h1
          className={`font-heading text-3xl md:text-4xl font-bold text-foreground-950 tracking-wide transition-all duration-[600ms] ease-out ${
            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "150ms" }}
        >
          {hero.title}
        </h1>
        <p
          className={`mt-3 text-base text-foreground-600 max-w-xl mx-auto transition-all duration-[600ms] ease-out ${
            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "300ms" }}
        >
          {hero.subtitle}
        </p>
      </div>
    </section>
  );
}

function BrandIntroSection() {
  const { brandIntro } = aboutPageData;
  const { ref, isVisible } = useEntranceAnimation({ threshold: 0.1, sectionIndex: 0 });

  return (
    <section
      ref={ref}
      className="relative flex items-center justify-center bg-background-50"
      style={{ minHeight: "70svh" }}
    >
      <div className="page-container py-16 md:py-20 w-full">
        <div className="max-w-3xl mx-auto">
          <div
            className={`space-y-5 text-foreground-700 leading-relaxed transition-all duration-[600ms] ease-out ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ transitionDelay: "0ms" }}
          >
            {brandIntro.paragraphs.map((para, i) => (
              <p key={i} className="text-base md:text-lg text-left">
                {para}
              </p>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PhilosophySection() {
  const { philosophy } = aboutPageData;
  const { ref, isVisible } = useEntranceAnimation({ threshold: 0.1, sectionIndex: 1 });

  const philosophyIcons = [
    "ri-lightbulb-flash-line",
    "ri-stack-line",
    "ri-time-line",
    "ri-group-line",
  ];

  return (
    <section
      ref={ref}
      className="relative flex items-center justify-center bg-background-100 overflow-hidden"
      style={{ minHeight: "90svh" }}
    >
      <div className="page-container py-16 md:py-20 w-full">
        <div
          className={`transition-all duration-[600ms] ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "0ms" }}
        >
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-wide text-center mb-3">
            {philosophy.title}
          </h2>
          <div className="flex items-center justify-center mb-10 md:mb-14">
            <span className="block w-10 h-[2px] bg-accent-400 rounded-full" />
            <span className="block w-16 h-[3px] bg-accent-500 rounded-full mx-2" />
            <span className="block w-10 h-[2px] bg-accent-400 rounded-full" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-4xl mx-auto">
          {philosophy.items.map((item, i) => (
            <div
              key={i}
              className={`group bg-background-50 rounded-lg p-6 md:p-7 border border-background-200/70 transition-all duration-[600ms] ease-out hover:border-accent-300/60 hover:-translate-y-1 ${
                isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-10 scale-95"
              }`}
              style={{ transitionDelay: `${120 + i * 120}ms` }}
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 group-hover:bg-accent-200 group-hover:text-accent-700 transition-colors duration-300">
                  <i className={`${philosophyIcons[i]} text-lg`}></i>
                </div>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2 group-hover:text-accent-700 transition-colors duration-300">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const directionDetails: Record<string, { icon: string; description: string }> = {
  "武俠小說（青城十九俠）": {
    icon: "ri-sword-line",
    description: "青城十九俠系列，透過人物、門派、冒險與江湖敘事，呈現武俠閱讀內容。",
  },
  "大眾閱讀": {
    icon: "ri-book-open-line",
    description: "通俗易懂的知識內容，協助讀者建立日常閱讀節奏，讓閱讀成為生活的一部分。",
  },
  "自然科普": {
    icon: "ri-leaf-line",
    description: "以平易近人的文字介紹自然現象、科學知識，從生活中探索世界的奧秘。",
  },
  "奇觀解謎": {
    icon: "ri-compass-3-line",
    description: "從自然奇觀與探索性題材出發，帶領讀者理解事件背後的知識與脈絡。",
  },
  "大華文苑書系": {
    icon: "ri-bookmark-line",
    description: "聚焦大眾閱讀、自然科普與奇觀解謎，兼具通俗性與知識探索趣味的圖書系列。",
  },
};

function DirectionCard({
  item,
  index,
}: {
  item: string;
  index: number;
}) {
  const detail = directionDetails[item];
  const { ref, isVisible } = useEntranceAnimation({
    threshold: 0.15,
    sectionIndex: index,
  });

  if (!detail) {
    return (
      <div
        ref={ref}
        className={`bg-background-100 rounded-lg p-5 border border-background-200/70 text-center transition-all duration-[600ms] ease-out ${
          isVisible
            ? "opacity-100 translate-y-0 scale-100"
            : "opacity-0 translate-y-12 scale-[0.92]"
        }`}
      >
        <span className="text-sm text-foreground-600">{item}</span>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={`group flex flex-col items-center text-center bg-background-100 rounded-xl px-5 py-7 border border-background-200/70 cursor-pointer transition-all duration-[600ms] ease-out hover:-translate-y-2 hover:border-accent-400/60 hover:bg-background-50 ${
        isVisible
          ? "opacity-100 translate-y-0 scale-100"
          : "opacity-0 translate-y-12 scale-[0.92]"
      }`}
    >
      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 group-hover:bg-accent-200 group-hover:text-accent-700 transition-all duration-300 mb-4">
        <i className={`${detail.icon} text-xl`}></i>
      </div>
      <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2 group-hover:text-accent-700 transition-colors duration-300">
        {item}
      </h3>
      <p className="text-xs text-foreground-600 leading-relaxed max-w-[200px]">
        {detail.description}
      </p>
    </div>
  );
}

function DirectionsSection() {
  const { directions } = aboutPageData;
  const { ref, isVisible } = useEntranceAnimation({ threshold: 0.1, sectionIndex: 2 });

  return (
    <section
      ref={ref}
      className="relative flex items-center justify-center bg-background-50 overflow-hidden"
      style={{ minHeight: "85svh" }}
    >
      {/* decorative background elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -left-20 w-72 h-72 rounded-full bg-accent-100/30 blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-80 h-80 rounded-full bg-secondary-100/20 blur-3xl" />
      </div>

      <div className="page-container py-16 md:py-20 w-full relative z-10">
        <div
          className={`transition-all duration-[600ms] ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "0ms" }}
        >
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-wide text-center mb-3">
            {directions.title}
          </h2>
          <div className="flex items-center justify-center mb-10 md:mb-14">
            <span className="block w-10 h-[2px] bg-accent-400 rounded-full" />
            <span className="block w-16 h-[3px] bg-accent-500 rounded-full mx-2" />
            <span className="block w-10 h-[2px] bg-accent-400 rounded-full" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5 max-w-5xl mx-auto">
          {directions.items.map((item, i) => (
            <DirectionCard key={i} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TargetReadersSection() {
  const { targetReaders } = aboutPageData;
  const { ref, isVisible } = useEntranceAnimation({ threshold: 0.1, sectionIndex: 3 });

  return (
    <section
      ref={ref}
      className="relative flex items-center justify-center bg-background-100"
      style={{ minHeight: "80svh" }}
    >
      <div className="page-container py-16 md:py-20 w-full">
        <div className="max-w-3xl mx-auto">
          <div
            className={`transition-all duration-[600ms] ease-out ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ transitionDelay: "0ms" }}
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-wide text-center mb-4">
              {targetReaders.title}
            </h2>
            <p className="text-center text-foreground-600 mb-8 md:mb-10">
              {targetReaders.description}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            {targetReaders.items.map((item, i) => (
              <div
                key={i}
                className={`flex items-start gap-3 bg-background-50 rounded-md p-4 border border-background-200/70 transition-all duration-[600ms] ease-out ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${80 + i * 60}ms` }}
              >
                <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 mt-0.5">
                  <i className="ri-check-line text-sm"></i>
                </div>
                <span className="text-sm text-foreground-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function BottomCtaBlock() {
  const navigate = useNavigate();
  const { bottomCta } = aboutPageData;
  const { ref, isVisible } = useEntranceAnimation({ threshold: 0.15, sectionIndex: 4 });

  return (
    <section
      ref={ref}
      className="relative flex items-center justify-center bg-foreground-950"
      style={{ minHeight: "50svh" }}
    >
      <div className="page-container py-16 md:py-20 text-center">
        <div
          className={`transition-all duration-[600ms] ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "0ms" }}
        >
          <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-wide mb-3 text-on-dark-primary">
            {bottomCta.title}
          </h2>
          <p className="text-sm md:text-base text-on-dark-secondary max-w-lg mx-auto mb-6 md:mb-8 leading-relaxed">
            {bottomCta.description}
          </p>
        </div>
        <div
          className={`flex flex-col sm:flex-row items-center justify-center gap-3 transition-all duration-[600ms] ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "120ms" }}
        >
          {bottomCta.buttons.map((btn) =>
            btn.external ? (
              <a
                key={btn.label}
                href={btn.href}
                target="_blank"
                rel="nofollow noopener"
                className="px-6 py-3 text-sm font-medium rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600"
              >
                {btn.label}
              </a>
            ) : (
              <button
                key={btn.label}
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate(btn.href);
                }}
                className="px-6 py-3 text-sm font-medium rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap bg-secondary-500 text-background-50 hover:bg-secondary-600"
              >
                {btn.label}
              </button>
            )
          )}
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <div className="bg-background-50">
      <AboutPageJsonLd />
      <AboutBreadcrumbJsonLd />
      <Navbar />
      <main>
        <AboutHero />
        <BrandIntroSection />
        <PhilosophySection />
        <DirectionsSection />
        <TargetReadersSection />
        <BottomCtaBlock />
      </main>
      <Footer />
    </div>
  );
}